/**
 * useWaypoints Hook
 *
 * Waypoint-specific operations hook
 * Provides CRUD operations, selection, and validation
 */

import { useMissionStore } from '@/stores/useMissionStore';
import type {
  Waypoint,
  WaypointInput,
  WaypointValidation,
} from '../types/waypoint.types';
import { validateWaypoint } from '../utils/waypoint-validator';

/**
 * Hook return type
 */
interface UseWaypointsReturn {
  // State
  waypoints: Waypoint[];
  selectedWaypoint: Waypoint | null;
  hoveredWaypoint: Waypoint | null;

  // CRUD Operations
  addWaypoint: (input: WaypointInput) => void;
  updateWaypoint: (waypointId: string, updates: Partial<Waypoint>) => void;
  deleteWaypoint: (waypointId: string) => void;
  reorderWaypoints: (startIndex: number, endIndex: number) => void;
  insertWaypoint: (afterIndex: number, input: WaypointInput) => void;

  // Selection
  selectWaypoint: (waypointId: string | null) => void;
  setHoveredWaypoint: (waypointId: string | null) => void;

  // Queries
  getWaypointById: (waypointId: string) => Waypoint | undefined;
  getNextSequence: () => number;

  // Validation
  validateWaypoint: (
    waypoint: Waypoint,
    prevWaypoint?: Waypoint
  ) => WaypointValidation;
}

/**
 * Waypoint operations hook
 */
export function useWaypoints(): UseWaypointsReturn {
  const mission = useMissionStore((state) => state.currentMission);
  const waypoints = mission?.waypoints ?? [];

  const selectedWaypointId = useMissionStore(
    (state) => state.selectedWaypointId
  );
  const hoveredWaypointId = useMissionStore((state) => state.hoveredWaypointId);

  const selectedWaypoint =
    waypoints.find((wp) => wp.id === selectedWaypointId) ?? null;
  const hoveredWaypoint =
    waypoints.find((wp) => wp.id === hoveredWaypointId) ?? null;

  const addWaypoint = useMissionStore((state) => state.addWaypoint);
  const updateWaypoint = useMissionStore((state) => state.updateWaypoint);
  const deleteWaypoint = useMissionStore((state) => state.deleteWaypoint);
  const reorderWaypoints = useMissionStore((state) => state.reorderWaypoints);
  const insertWaypoint = useMissionStore((state) => state.insertWaypoint);

  const selectWaypoint = useMissionStore((state) => state.selectWaypoint);
  const setHoveredWaypoint = useMissionStore(
    (state) => state.setHoveredWaypoint
  );

  const getWaypointById = useMissionStore((state) => state.getWaypointById);
  const getNextSequence = useMissionStore((state) => state.getNextSequence);

  return {
    waypoints,
    selectedWaypoint,
    hoveredWaypoint,
    addWaypoint,
    updateWaypoint,
    deleteWaypoint,
    reorderWaypoints,
    insertWaypoint,
    selectWaypoint,
    setHoveredWaypoint,
    getWaypointById,
    getNextSequence,
    validateWaypoint,
  };
}
